// Страница управления уровнями сложности для администраторов: CRUD уровней с инлайн-ошибкой при удалении используемого уровня.

import { useEffect, useState } from 'react';
import {
  Container,
  Table,
  Button,
  Spinner,
  Alert,
  Form,
  Modal,
} from 'react-bootstrap';
import AppNavbar from '../../components/common/AppNavbar';
import {
  getComplexityLevels,
  createComplexityLevel,
  updateComplexityLevel,
  deleteComplexityLevel,
} from '../../api/exerciseApi';

export default function AdminComplexityLevelsPage() {
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form modal state (create / edit)
  const [formModal, setFormModal] = useState(null);
  // formModal shape: { mode: 'create' | 'edit', level?: { id, name } }
  const [formName, setFormName] = useState('');
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Delete confirm modal state
  const [deleteModal, setDeleteModal] = useState(null);
  // deleteModal shape: { level: { id, name } }
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    loadLevels();
  }, []);

  async function loadLevels() {
    setLoading(true);
    setError('');
    try {
      const res = await getComplexityLevels();
      setLevels(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail ?? 'Не удалось загрузить уровни сложности');
    } finally {
      setLoading(false);
    }
  }

  // ── Form modal handlers ──────────────────────────────────────────────────────

  const openCreate = () => {
    setFormName('');
    setFormError('');
    setFormModal({ mode: 'create' });
  };

  const openEdit = (level) => {
    setFormName(level.name);
    setFormError('');
    setFormModal({ mode: 'edit', level });
  };

  const closeFormModal = () => {
    if (formSaving) return;
    setFormModal(null);
    setFormError('');
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formName.trim()) {
      setFormError('Введите название уровня');
      return;
    }
    setFormSaving(true);
    setFormError('');
    try {
      if (formModal.mode === 'create') {
        await createComplexityLevel({ name: formName.trim() });
      } else {
        await updateComplexityLevel(formModal.level.id, { name: formName.trim() });
      }
      await loadLevels();
      setFormModal(null);
    } catch (err) {
      setFormError(err?.response?.data?.detail ?? 'Ошибка при сохранении');
    } finally {
      setFormSaving(false);
    }
  };

  // ── Delete modal handlers ────────────────────────────────────────────────────

  const openDelete = (level) => {
    setDeleteError('');
    setDeleteModal({ level });
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteModal(null);
    setDeleteError('');
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteComplexityLevel(deleteModal.level.id);
      setLevels((prev) => prev.filter((l) => l.id !== deleteModal.level.id));
      setDeleteModal(null);
    } catch (err) {
      setDeleteError(
        err?.response?.data?.detail ?? 'Не удалось удалить уровень. Возможно, он используется в упражнениях.'
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <AppNavbar />

      <div style={{ background: 'var(--ewl-bg-body)', minHeight: 'calc(100vh - 56px)' }}>
        <Container className="py-4">

          {/* ── Заголовок ── */}
          <div
            className="d-flex justify-content-between align-items-center mb-4 pb-3"
            style={{ borderBottom: '1px solid #e9ecef' }}
          >
            <div>
              <h5 className="fw-bold mb-0" style={{ color: '#1e2a4a', letterSpacing: '-0.01em' }}>
                Уровни сложности
              </h5>
              <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.875rem' }}>
                {loading
                  ? 'Загрузка...'
                  : `${levels.length} ${levels.length === 1 ? 'уровень' : levels.length >= 2 && levels.length <= 4 ? 'уровня' : 'уровней'}`}
              </p>
            </div>
            <Button
              variant="dark"
              onClick={openCreate}
              style={{ borderRadius: '0.5rem' }}
            >
              + Добавить уровень
            </Button>
          </div>

          {/* ── Состояния ── */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                Загрузка уровней сложности...
              </p>
            </div>
          ) : error ? (
            <Alert variant="danger" style={{ borderRadius: '0.875rem' }}>
              {error}
            </Alert>
          ) : levels.length === 0 ? (
            <div
              className="text-center py-5"
              style={{
                background: '#fff',
                borderRadius: '0.875rem',
                boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
              }}
            >
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
              <h6 className="fw-semibold text-muted mb-1">Уровней сложности нет</h6>
              <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                Создайте первый уровень сложности
              </p>
              <Button variant="dark" size="sm" onClick={openCreate}>
                + Добавить уровень
              </Button>
            </div>
          ) : (

            /* ── Таблица ── */
            <div className="table-wrapper">
              <Table hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '3rem' }}>#</th>
                    <th>Название</th>
                    <th style={{ width: '14rem' }}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {levels.map((level, idx) => (
                    <tr key={level.id}>
                      <td className="text-muted" style={{ fontSize: '0.875rem' }}>
                        {idx + 1}
                      </td>
                      <td className="fw-medium" style={{ color: '#1e2a4a' }}>
                        {level.name}
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          onClick={() => openEdit(level)}
                          style={{ borderRadius: '0.4rem', fontWeight: 500, fontSize: '0.8rem' }}
                        >
                          Редактировать
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => openDelete(level)}
                          style={{ borderRadius: '0.4rem', fontSize: '0.8rem' }}
                        >
                          Удалить
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Container>
      </div>

      {/* ── Модальное окно создания / редактирования ── */}
      <Modal show={Boolean(formModal)} onHide={closeFormModal}>
        <Form onSubmit={handleFormSubmit}>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600 }}>
              {formModal?.mode === 'create' ? 'Новый уровень сложности' : 'Редактировать уровень'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {formError && (
              <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: '0.875rem' }}>
                {formError}
              </Alert>
            )}
            <Form.Group>
              <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>
                Название *
              </Form.Label>
              <Form.Control
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Например: Beginner"
                style={{ borderRadius: '0.5rem' }}
                autoFocus
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" type="button" onClick={closeFormModal} disabled={formSaving}>
              Отмена
            </Button>
            <Button type="submit" variant="dark" disabled={formSaving} style={{ fontWeight: 500 }}>
              {formSaving ? <Spinner size="sm" animation="border" /> : 'Сохранить'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ── Модальное окно подтверждения удаления ── */}
      <Modal show={Boolean(deleteModal)} onHide={closeDeleteModal}>
        <Modal.Header closeButton>
          <div>
            <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600 }}>
              Удалить уровень сложности
            </Modal.Title>
            <div className="text-muted mt-1" style={{ fontSize: '0.875rem' }}>
              {deleteModal?.level?.name}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-4">
          {deleteError && (
            <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: '0.875rem' }}>
              {deleteError}
            </Alert>
          )}
          <p className="mb-0" style={{ fontSize: '0.9375rem' }}>
            Уровень сложности <strong>{deleteModal?.level?.name}</strong> будет удалён.
            Если он используется в упражнениях, удаление будет невозможно.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDeleteModal} disabled={deleting}>
            Отмена
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            style={{ fontWeight: 500 }}
          >
            {deleting ? <Spinner size="sm" animation="border" /> : 'Удалить'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
